"use client";

import logger from '../../../utils/logger';
import { useState, useEffect } from "react";
import { FaPlus, FaRegFolder, FaEdit, FaTrash } from "react-icons/fa";
import { HiDotsVertical } from "react-icons/hi";
import { IoMdCloseCircleOutline } from "react-icons/io";
import folderService from "../../../services/folderServices";
import commonFolderService from "../../../services/commonFolderServices";
import { useMeetingContext } from "../../../context/MeetingContext";
import { useSiteContext } from "../../../context/SiteContext";
import { Link, useNavigate } from "react-router-dom";
import { TfiAngleLeft } from "react-icons/tfi";
import { IoMdClose } from "react-icons/io";
import FolderIcon from "../../../images/icons/folder.svg";
import pollServices from "../../../services/pollServices";

const FolderList = ({ onFolderSelect }) => {
  const {
    getFolders,
    loading,
    setLoading,
    error,
    setError,
    folders,
    setCtaUpdateData,
    setUpdatePresetId,
  } = useMeetingContext();
  const { user } = useSiteContext();
  const navigate = useNavigate();

  const [selectedFolder, setSelectedFolder] = useState(null);
  // const [folders, setFolders] = useState([]);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [menuOpenFor, setMenuOpenFor] = useState(null);

  // Form states
  const [newFolderName, setNewFolderName] = useState("");
  const [editFolderName, setEditFolderName] = useState("");
  const [hasFolders, setHasFolders] = useState(false);

  // Polls states
  const [polls, setPolls] = useState([]);
  const [loadingPolls, setLoadingPolls] = useState(false);
  const [pollMenuOpenFor, setPollMenuOpenFor] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [deletingPollId, setDeletingPollId] = useState(null);

  // Common folders states
  const [commonFolders, setCommonFolders] = useState([]);
  const [loadingCommonFolders, setLoadingCommonFolders] = useState(false);

  useEffect(() => {
    setUpdatePresetId("");
  }, [setUpdatePresetId]);

  // Load folders on component mount
  useEffect(() => {
    getFolders();
  }, [getFolders]);

  useEffect(() => {
    const handleClickOutside = () => {
      setMenuOpenFor(null);
    };

    if (menuOpenFor) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [menuOpenFor]);

  useEffect(() => {
    setCtaUpdateData(null);
  }, [setCtaUpdateData]);

  // Close poll menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setPollMenuOpenFor(null);
    };

    if (pollMenuOpenFor) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [pollMenuOpenFor]);

  // Create folder
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      setError("Folder name is required");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const folderData = {
        name: newFolderName.trim(),
      };

      await folderService.createFolder(folderData);

      setShowCreateModal(false);
      setNewFolderName("");
      getFolders();
    } catch (err) {
      setError(err.message || "Failed to create folder");
    } finally {
      setLoading(false);
    }
  };

  // Update folder
  const handleUpdateFolder = async () => {
    if (!editingFolder || !editFolderName.trim()) {
      setError("Folder name is required");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const updateData = {
        name: editFolderName.trim(),
      };

      const response = await folderService.updateFolder(
        editingFolder._id,
        updateData
      );

      if (response) {
        getFolders();
        setShowEditModal(false);
        setEditingFolder(null);
        setEditFolderName("");
      }
    } catch (err) {
      setError(err.message || "Failed to update folder");
    } finally {
      setLoading(false);
    }
  };

  // Delete folder
  const handleDeleteFolder = async () => {
    if (!selectedFolder) {
      setError("No folder selected for deletion");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await folderService.deleteFolder(selectedFolder);

      if (response) {
        getFolders();
        setSelectedFolder(null);
        setShowDeleteModal(false);
      }
    } catch (err) {
      setError(err.message || "Failed to delete folder");
    } finally {
      setLoading(false);
    }
  };

  // Open edit modal
  const openEditModal = (folder) => {
    setEditingFolder(folder);
    setEditFolderName(folder.name);
    setShowEditModal(true);
  };

  // Open delete modal
  const openDeleteModal = (selectedFolder) => {
    if (!selectedFolder) {
      setError("Please select a folder to delete");
      return;
    }
    setShowDeleteModal(true);
  };

  const handleFolderClick = (folder) => {
    setSelectedFolder(folder._id);
    onFolderSelect?.(folder);
  };

  useEffect(() => {
    setHasFolders(folders.length > 0);
  }, [folders]);

  // Fetch polls if user is on Business plan
  useEffect(() => {
    if (user?.plan === "Business") {
      fetchPolls();
    }
  }, [user?.plan]);

  // Fetch common folders on mount
  useEffect(() => {
    fetchCommonFolders();
  }, []);

  const fetchCommonFolders = async () => {
    setLoadingCommonFolders(true);
    try {
      const response = await commonFolderService.getCommonFolders();
      // Handle both array response and object with folders property
      let foldersList = Array.isArray(response) 
        ? response 
        : (response?.folders || response?.data || []);
      
      // Sort folders by sortorder (ascending), with null/undefined values last
      foldersList = foldersList.sort((a, b) => {
        const orderA = a.sortorder ?? a.sortOrder ?? null;
        const orderB = b.sortorder ?? b.sortOrder ?? null;
        
        // If both have sortorder, sort by it
        if (orderA !== null && orderB !== null) {
          return orderA - orderB;
        }
        // If only A has sortorder, A comes first
        if (orderA !== null && orderB === null) {
          return -1;
        }
        // If only B has sortorder, B comes first
        if (orderA === null && orderB !== null) {
          return 1;
        }
        // If neither has sortorder, maintain original order
        return 0;
      });
      
      setCommonFolders(foldersList);
    } catch (err) {
      logger("error", "Error fetching common folders:", err);
      setCommonFolders([]);
    } finally {
      setLoadingCommonFolders(false);
    }
  };

  const fetchPolls = async () => {
    setLoadingPolls(true);
    try {
      const response = await pollServices.getPolls();
      if (response.success && response.data) {
        setPolls(Array.isArray(response.data) ? response.data : []);
      } else if (Array.isArray(response)) {
        setPolls(response);
      } else if (response.data && Array.isArray(response.data)) {
        setPolls(response.data);
      } else {
        setPolls([]);
      }
    } catch (err) {
      logger("error", "Error fetching polls:", err);
      setPolls([]);
    } finally {
      setLoadingPolls(false);
    }
  };

  const handleDeletePoll = async (pollId, e) => {
    if (e) {
      e.stopPropagation();
    }
    setPollMenuOpenFor(null);
    setDeleteConfirmId(pollId);
  };

  const confirmDeletePoll = async (pollId, e) => {
    if (e) {
      e.stopPropagation();
    }
    setDeletingPollId(pollId);
    try {
      const response = await pollServices.deletePoll(pollId);
      if (response.success) {
        setPolls(polls.filter((poll) => (poll._id || poll.id) !== pollId));
      } else {
        setError(response.message || "Failed to delete poll");
      }
    } catch (err) {
      setError(err.message || "Failed to delete poll");
    } finally {
      setDeleteConfirmId(null);
      setDeletingPollId(null);
    }
  };

  const cancelDeletePoll = (e) => {
    if (e) {
      e.stopPropagation();
    }
    setDeleteConfirmId(null);
  };

  const DeleteFolderModal = () => {
    const folderToDelete = folders.find((f) => f._id === selectedFolder);

    return (
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div className="bg-[#00000038] text-white rounded-lg p-4 md:p-6 w-full max-w-md">
          <div className="flex items-center justify-between mb-3 md:mb-4">
            <h3 className="text-base md:text-lg font-semibold">
              Delete Folder
            </h3>
            <button
              onClick={() => setShowDeleteModal(false)}
              className="p-1 hover:bg-gray-700 rounded"
            >
              <IoMdCloseCircleOutline className="w-4 h-4 md:w-5 md:h-5" />
            </button>
          </div>

          <p className="text-gray-300 mb-4 md:mb-6 text-sm md:text-base">
            Are you sure you want to delete &quot;{folderToDelete?.name}&quot;?
            This action cannot be undone.
          </p>

          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <button
              onClick={() => setShowDeleteModal(false)}
              className="w-full sm:flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors text-sm md:text-base"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteFolder}
              disabled={loading}
              className="w-full sm:flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 rounded-lg transition-colors text-sm md:text-base"
            >
              {loading ? "Deleting..." : "Delete Folder"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const displayFolders = folders.length > 0 ? folders : [];

  return (
    <div className="h-screen ">
      <div className="w-full max-w-8xl mx-auto p-3 md:p-6 primary-card-background rounded-xl md:rounded-2xl h-[calc(100vh-100px)] overflow-y-auto">
        {/* Error Display */}
        {error && (
          <div className="mb-3 md:mb-4 p-3 md:p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300 text-sm md:text-base">
            {error}
            <button
              onClick={() => setError(null)}
              className="ml-2 text-red-400 hover:text-red-300"
            >
              ×
            </button>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 md:mb-8 gap-3 md:gap-0">
          <h1 className="text-xl md:text-2xl font-semibold flex gap-3 md:gap-5 items-center uppercase text-white">
            <span>ELEMENTS</span>
            <span>
              <TfiAngleLeft />
            </span>
          </h1>

          <div className="flex items-center text-white gap-2 md:gap-4">
            {/* Create Poll Button - Only for Business plan */}
            {user?.plan === "Business" && (
              <button
                onClick={() => navigate("/dashboard/create-poll")}
                className="flex items-center gap-2 px-3 md:px-4 py-2 bg-[#7700FF] hover:bg-[#6600DD] rounded-lg transition-colors text-sm md:text-base"
              >
                <span className="font-medium">Create Poll</span>
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                  />
                </svg>
              </button>
            )}
            {/* New Folder Button */}
            {hasFolders && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-3 md:px-4 py-2 bg-[#785E82] rounded-lg transition-colors text-sm md:text-base"
              >
                <span className="font-medium">Create New</span>
                <img src={FolderIcon} alt="Folder" className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Loading State */}
        {loading && folders.length === 0 && (
          <div className="text-center py-6 md:py-8">
            <div className="animate-spin rounded-full h-6 w-6 md:h-8 md:w-8 border-b-2 border-white mx-auto"></div>
            <p className="mt-2 text-gray-400 text-sm md:text-base">
              Loading folders...
            </p>
          </div>
        )}

        {/* Folder Grid */}
        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-6">
            {displayFolders.map((folder) => (
              <div
                key={folder._id}
                className={`p-3 md:p-4 rounded-lg md:rounded-xl cursor-pointer transition-all duration-200 relative group bg-[#6F5CA4]`}
              >
                <Link to={`/dashboard/folders/element/${folder._id}`}>
                  <div className="flex items-start gap-2 md:gap-3">
                    <div className="w-7 h-7 md:w-8 md:h-8 bg-white rounded-md md:rounded-lg flex items-center justify-center flex-shrink-0">
                      <FaRegFolder className="text-xs md:text-sm" />
                    </div>
                    <div className="flex-1 text-white min-w-0">
                      <h3 className="font-medium truncate text-sm md:text-base">
                        {folder.name}
                      </h3>
                      <p className="text-xs md:text-sm mt-1">Created by me</p>
                    </div>
                  </div>

                  {/* Actions Menu */}
                  <div className="absolute z-10 top-2 right-2">
                    <div className="relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault(); // Prevent default link navigation
                          setMenuOpenFor(
                            menuOpenFor === folder._id ? null : folder._id
                          );
                        }}
                        className="p-1 bg-gray-700/80 hover:bg-gray-600/80 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                        title="Actions"
                      >
                        <HiDotsVertical className="w-3 h-3" />
                      </button>

                      {menuOpenFor === folder._id && (
                        <div
                          className="absolute z-20 top-8 right-2 bg-[#2A2835] border border-gray-600 text-white rounded-md shadow-lg w-28"
                          style={{
                            boxShadow:
                              "0 10px 25px rgba(0, 0, 0, 0.5), 0 0 10px rgba(0, 0, 0, 0.3)",
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              openEditModal(folder);
                              setMenuOpenFor(null);
                            }}
                            className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-700"
                          >
                            Edit
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              setSelectedFolder(folder._id);
                              openDeleteModal(folder._id);
                              setMenuOpenFor(null);
                            }}
                            className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-700"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Click handler */}
                  <div
                    className="absolute inset-0"
                    onClick={() => handleFolderClick(folder)}
                  />
                </Link>
              </div>
            ))}
          </div>
        )}

        {/* Common Folders Section - Below Regular Folders */}
        {!loading && commonFolders.length > 0 && (
          <div className="mt-6 md:mt-8">
            <div className="mb-3 md:mb-4 flex items-center gap-2">
              <h2 className="text-sm md:text-base font-semibold text-white uppercase">
                COMMON FOLDERS
              </h2>
              <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-[10px] md:text-xs rounded-full border border-blue-500/30">
                Shared
              </span>
            </div>

            {loadingCommonFolders ? (
              <div className="text-center py-6">
                <div className="animate-spin rounded-full h-5 w-5 md:h-6 md:w-6 border-b-2 border-white mx-auto"></div>
                <p className="mt-2 text-gray-400 text-xs md:text-sm">
                  Loading common folders...
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-6">
                {commonFolders.map((folder) => (
                  <div
                    key={folder._id}
                    className={`p-3 md:p-4 rounded-lg md:rounded-xl cursor-pointer transition-all duration-200 relative group bg-[#5A4A6B] border border-blue-500/20`}
                  >
                    <Link to={`/dashboard/common-folders/element/${folder._id}`}>
                      <div className="flex items-start gap-2 md:gap-3">
                        <div className="w-7 h-7 md:w-8 md:h-8 bg-white rounded-md md:rounded-lg flex items-center justify-center flex-shrink-0">
                          <FaRegFolder className="text-xs md:text-sm" />
                        </div>
                        <div className="flex-1 text-white min-w-0">
                          <h3 className="font-medium truncate text-sm md:text-base">
                            {folder.name}
                          </h3>
                          <p className="text-xs md:text-sm mt-1 text-gray-300">Common folder</p>
                        </div>
                      </div>

                      {/* Click handler */}
                      <div
                        className="absolute inset-0"
                        onClick={() => handleFolderClick(folder)}
                      />
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Polls Section - Below Folders */}
        {user?.plan === "Business" && !loading && (
          <div className="mt-6 md:mt-8">
            <div className="mb-3 md:mb-4">
              <h2 className="text-sm md:text-base font-semibold text-white uppercase">
                ALL POLLS
              </h2>
            </div>

            <div>
              {loadingPolls ? (
                <div className="text-center py-6">
                  <div className="animate-spin rounded-full h-5 w-5 md:h-6 md:w-6 border-b-2 border-white mx-auto"></div>
                  <p className="mt-2 text-gray-400 text-xs md:text-sm">
                    Loading polls...
                  </p>
                </div>
              ) : polls.length === 0 ? (
                <div className="text-center py-8">
                  <svg
                    className="h-12 w-12 text-gray-500 mx-auto mb-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                  <p className="text-gray-400 text-sm md:text-base mb-3">
                    No polls found
                  </p>
                  <button
                    onClick={() => navigate("/dashboard/create-poll")}
                    className="px-3 py-1.5 bg-[#7700FF] text-white rounded-lg transition-colors font-semibold hover:bg-[#6600DD] flex items-center justify-center space-x-2 mx-auto text-sm"
                  >
                    <FaPlus className="w-3 h-3" />
                    <span>Create Your First Poll</span>
                  </button>
                </div>
              ) : (
                <div className="space-y-2 md:space-y-3">
                  {polls.map((poll) => {
                    const pollId = poll._id || poll.id;
                    const formatDate = (dateString) => {
                      if (!dateString) return "N/A";
                      try {
                        const date = new Date(dateString);
                        return date.toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        });
                      } catch (err) {
                        return dateString;
                      }
                    };

                    const getStatusBadge = (status) => {
                      const statusLower = status?.toLowerCase() || "inactive";
                      if (statusLower === "active") {
                        return (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] md:text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
                            <svg
                              className="h-2.5 w-2.5 mr-1"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                            Active
                          </span>
                        );
                      } else if (statusLower === "closed") {
                        return (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] md:text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/30">
                            <svg
                              className="h-2.5 w-2.5 mr-1"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                            Closed
                          </span>
                        );
                      } else {
                        return (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] md:text-xs font-medium bg-gray-500/20 text-gray-400 border border-gray-500/30">
                            <svg
                              className="h-2.5 w-2.5 mr-1"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                            {status || "Inactive"}
                          </span>
                        );
                      }
                    };

                    const toggleMenu = (e) => {
                      e.stopPropagation();
                      setPollMenuOpenFor(
                        pollMenuOpenFor === pollId ? null : pollId
                      );
                    };

                    const handleEditPoll = (e) => {
                      e.stopPropagation();
                      setPollMenuOpenFor(null);
                      navigate(`/dashboard/edit-poll/${pollId}`, {
                        state: { poll },
                      });
                    };

                    return (
                      <div
                        key={pollId}
                        className="p-3 md:p-4 bg-gray-800/50 rounded-lg border border-gray-700 hover:border-gray-600 hover:bg-gray-800/60 transition-all relative group"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <h3 className="text-white font-medium text-sm md:text-base flex-1 truncate pr-2">
                                {poll.question || "Untitled Poll"}
                              </h3>
                              <div className="flex items-center gap-1.5 flex-shrink-0">
                                {getStatusBadge(poll.status)}
                                {/* Actions Menu */}
                                <div className="relative">
                                  <button
                                    onClick={toggleMenu}
                                    className="p-1 hover:bg-gray-700 rounded transition-colors"
                                    title="More options"
                                  >
                                    <HiDotsVertical className="h-4 w-4 text-gray-400" />
                                  </button>

                                  {pollMenuOpenFor === pollId && (
                                    <div
                                      className="absolute right-0 mt-1 w-40 bg-gray-800 rounded-lg shadow-lg border border-gray-700 z-50"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <button
                                        onClick={handleEditPoll}
                                        className="w-full px-3 py-2 text-left text-xs text-white hover:bg-gray-700 flex items-center gap-2 rounded-t-lg transition-colors"
                                      >
                                        <FaEdit className="h-3.5 w-3.5" />
                                        Edit Poll
                                      </button>
                                      <button
                                        onClick={(e) =>
                                          handleDeletePoll(pollId, e)
                                        }
                                        className="w-full px-3 py-2 text-left text-xs text-red-400 hover:bg-gray-700 flex items-center gap-2 rounded-b-lg transition-colors"
                                      >
                                        <FaTrash className="h-3.5 w-3.5" />
                                        Delete Poll
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center gap-4 text-xs text-gray-400">
                              <div className="flex items-center gap-1.5">
                                <svg
                                  className="h-3 w-3"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                                  />
                                </svg>
                                <span>{poll.options?.length || 0} options</span>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <svg
                                  className="h-3 w-3"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                  />
                                </svg>
                                <span className="truncate">
                                  {formatDate(
                                    poll.createdAt || poll.created_at
                                  )}
                                </span>
                              </div>
                            </div>

                            {poll.options && poll.options.length > 0 && (
                              <div className="mt-2.5">
                                <div className="flex flex-wrap gap-1.5">
                                  {poll.options
                                    .slice(0, 3)
                                    .map((option, index) => (
                                      <span
                                        key={index}
                                        className="px-2 py-0.5 bg-gray-700/60 rounded text-[10px] md:text-xs text-gray-300 truncate max-w-[120px]"
                                      >
                                        {typeof option === "string"
                                          ? option
                                          : option.text ||
                                            option.label ||
                                            `Option ${index + 1}`}
                                      </span>
                                    ))}
                                  {poll.options.length > 3 && (
                                    <span className="px-2 py-0.5 bg-gray-700/60 rounded text-[10px] md:text-xs text-gray-300">
                                      +{poll.options.length - 3} more
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Delete Confirmation Modal */}
                        {deleteConfirmId === pollId && (
                          <div
                            className="absolute inset-0 bg-black/60 rounded-lg flex items-center justify-center z-20"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="bg-gray-800 p-4 md:p-6 rounded-lg border border-gray-700 max-w-sm mx-4">
                              <h4 className="text-white font-semibold text-base md:text-lg mb-2">
                                Delete Poll?
                              </h4>
                              <p className="text-gray-400 text-xs md:text-sm mb-4">
                                Are you sure you want to delete this poll? This
                                action cannot be undone.
                              </p>
                              <div className="flex gap-3 justify-end">
                                <button
                                  onClick={cancelDeletePoll}
                                  disabled={deletingPollId === pollId}
                                  className="px-3 md:px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors text-xs md:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={(e) => confirmDeletePoll(pollId, e)}
                                  disabled={deletingPollId === pollId}
                                  className="px-3 md:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-xs md:text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                  {deletingPollId === pollId ? (
                                    <>
                                      <svg
                                        className="animate-spin h-3 w-3 md:h-4 md:w-4"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                      >
                                        <circle
                                          className="opacity-25"
                                          cx="12"
                                          cy="12"
                                          r="10"
                                          stroke="currentColor"
                                          strokeWidth="4"
                                        ></circle>
                                        <path
                                          className="opacity-75"
                                          fill="currentColor"
                                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        ></path>
                                      </svg>
                                      Deleting...
                                    </>
                                  ) : (
                                    "Delete"
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading &&
          displayFolders.length === 0 &&
          !showCreateModal &&
          polls.length === 0 && (
            <div className="text-center py-12 md:py-20 h-full flex flex-col items-center justify-center px-4">
              <div className="flex justify-center items-center mb-3 md:mb-4">
                <img
                  src={FolderIcon}
                  alt="Folder"
                  className="w-12 h-12 md:w-16 md:h-16"
                />
              </div>
              <h3 className="text-xl md:text-2xl font-medium mb-2 text-white">
                Create a folder to get started
              </h3>
              <p className="text-white mb-6 md:mb-8 mx-auto text-sm md:text-base max-w-md">
                Keep all your elements and files organized in one place, whether
                for a brand, event, or team. Add more anytime you like.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                {user?.plan === "Business" && (
                  <button
                    onClick={() => navigate("/dashboard/create-poll")}
                    className="flex items-center gap-2 px-4 md:px-6 py-2.5 md:py-3 bg-[#7700FF] hover:bg-[#6600DD] rounded-lg transition-colors text-white text-sm md:text-base"
                  >
                    <span className="font-medium">Create Poll</span>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                      />
                    </svg>
                  </button>
                )}
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="flex items-center gap-2 px-4 md:px-6 py-2.5 md:py-3 bg-green-500 hover:bg-green-600 rounded-lg transition-colors text-white text-sm md:text-base"
                >
                  <span className="font-medium">Create Folder</span>
                  <FaPlus className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

        {/* Modals */}
        {showCreateModal && (
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-[#615B72] relative rounded-lg p-4 md:p-6 w-full max-w-md">
              <div className="absolute top-2 right-2 md:top-3 md:right-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="p-1 rounded cursor-pointer"
                >
                  <IoMdClose
                    color="white"
                    size={20}
                    className="md:w-6 md:h-6"
                  />
                </button>
              </div>

              <div className="flex items-center justify-center mb-3 md:mb-4">
                <img
                  src={FolderIcon}
                  alt="Folder"
                  className="w-12 h-12 md:w-auto md:h-auto"
                />
              </div>

              <div className="space-y-3 md:space-y-4">
                <div>
                  <label className="block text-base md:text-lg text-white font-bold mb-2">
                    Name your folder *
                  </label>
                  <input
                    type="text"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500 text-sm md:text-base"
                    placeholder="Enter folder name"
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-4 md:mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="w-full sm:flex-1 px-4 py-2 border border-red-150 text-red-150 font-bold rounded-lg transition-colors cursor-pointer text-sm md:text-base"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateFolder}
                  disabled={loading}
                  className="w-full sm:flex-1 px-4 py-2 bg-green-500 hover:bg-green-600 disabled:bg-gray-600 rounded-lg transition-colors text-white font-bold cursor-pointer text-sm md:text-base"
                >
                  {loading ? "Creating..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        )}
        {showEditModal && (
          <div className="fixed inset-0 flex items-center justify-center z-50 bg-[#00000070] p-4">
            <div className="bg-[#00000038] text-white rounded-lg p-4 md:p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-3 md:mb-4">
                <h3 className="text-base md:text-lg font-semibold">
                  Edit Folder
                </h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="p-1 hover:bg-gray-700 rounded"
                >
                  <IoMdCloseCircleOutline className="w-4 h-4 md:w-5 md:h-5" />
                </button>
              </div>

              <div className="space-y-3 md:space-y-4">
                <div>
                  <label className="block text-xs md:text-sm font-medium mb-2">
                    Folder Name *
                  </label>
                  <input
                    type="text"
                    value={editFolderName}
                    onChange={(e) => setEditFolderName(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500 text-sm md:text-base"
                    placeholder="Enter folder name"
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-4 md:mt-6">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="w-full sm:flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors text-sm md:text-base"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateFolder}
                  disabled={loading}
                  className="w-full sm:flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 rounded-lg transition-colors text-sm md:text-base"
                >
                  {loading ? "Updating..." : "Update Folder"}
                </button>
              </div>
            </div>
          </div>
        )}
        {showDeleteModal && <DeleteFolderModal />}
      </div>
    </div>
  );
};

export default FolderList;
